import 'regenerator-runtime/runtime';
import { NOT_FOUND, redirect } from 'redux-first-router';
import * as Sentry from '@sentry/browser';
import { changeCountry, logoutAccount } from './actions/checkout';
import { fetchData, fetchCheckout, fetchDepict } from './utils/async';
import { getCookie, setCookie, deleteAllCookies } from './utils/dom';
import { translate, removeLanguagePrefix } from './utils/locale';
import { createSha256Hash } from './utils/string';
import { deepClone, hasTemplateInObj } from './utils/object';

// Customer specific data is loaded in frontend.
// Generic data for all clients is SSR
const clientUpdate = (dispatch, getState, pageType = '') => {
	dispatch({ type: 'CLEAR_ERRORS' });

	const { location = {}, environment = {}, checkout = {} } = getState();
	const { setup = {} } = environment;
	const pageTypeMatrix = {
		searchResults: 'srp',
		productDetail: 'pdp',
		category: 'cat',
		subCategory: 'cat',
		cart: 'co',
		receipt: 'ord',
	};

	if (location.type === 'START' || location.type === 'START2') pageType = 'homepage';
	else if (location.type === 'SEARCH') pageType = 'searchResults';
	else if (location.type === 'SELECTION') pageType = 'cart';
	else if (location.type === 'RECEIPT') pageType = 'receipt';
	else if (pageType === 'product') pageType = 'productDetail';
	else if (pageType === 'category' && removeLanguagePrefix(location.pathname).split('/').length > 1) pageType = 'subCategory';
	else if (pageType === 'category') pageType = 'category';

	const sendData = {
		enterpriseId: 1565337,
		pageType,
		emailHash: typeof window !== 'undefined' ? window.localStorage.getItem('hash') : '',
	};

	if (pageType === 'cart') {
		const { selection } = getState().checkout;

		if (selection.items) {
			sendData.cartSubtotal = `${selection.items.reduce((acc, item) => (acc + (item.priceEachWithoutTaxAsNumber * item.quantity)), 0).toFixed(2)}`;
			sendData.items = selection.items.map((item) => ({ unitPrice: item.priceEachWithoutTaxAsNumber, itemId: item.sku, quantity: item.quantity }));
		}
	}

	if (typeof window !== 'undefined') {
		if (window.fitPredictorInitiated && location.kind !== 'load') window.fitPredictorInitiated = false;

		if (pageType !== '') {
			if (!window.cj) window.cj = {};
			window.cj.sitePage = sendData;
			if (window.cjApi) window.cjApi.sendOrder(window.cj.sitePage);
		}

		const setupFitPredictor = () => {
			if (typeof window.ssp === 'undefined' || (!window.fitPredictorInitiated && pageType === 'productDetail')) {
				setTimeout(() => {
					setupFitPredictor();
				}, 200);
				return;
			}

			window.ssp('set', 'site', {
				currency: setup.currency,
				language: setup.language.slice(0, 2),
				layout: setup.breakPoint,
				market: 'WW',
			});

			if (checkout?.payment?.address?.email) {
				createSha256Hash((checkout?.payment?.address?.email || '').trim().toLowerCase())
					.then((emailHash) => {
						window.ssp('set', 'user', {
							emailHash,
						});
						return emailHash;
					}).catch(() => console.log('ERROR: unable to hash email aborted.'));
			}

			if (pageTypeMatrix[pageType]) {
				window.ssp('set', 'page', {
					type: pageTypeMatrix[pageType],
				});
			}

			window.ssp('start', 'fitpredictor');
		};

		setupFitPredictor();

		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		window.dataLayer.push({
			event: 'PageView',
			values: {
				path: location.pathname,
				pageType,
			},
		});
	}
};

const updatePaymentInfo = (dispatch, selectionData, token) => {
	const paymentInfo = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('paymentInfo')) : false;
	if (paymentInfo) {
		if (selectionData?.selection?.items && selectionData.selection.items.length > 0 && paymentInfo?.additionalInformation && paymentInfo.additionalInformation.length > 0) {
			// Ensure custom product data is synced with basket.
			const additionalInformation = paymentInfo.additionalInformation.filter((item) => selectionData.selection.items.filter((sItem) => sItem.item === item.item).length > 0);
			dispatch({ type: 'INIT_PAYMENT_INFO', paymentInfo: { ...paymentInfo, additionalInformation } });
		} else if ((!token || !selectionData?.selection?.items || selectionData.selection.items.length === 0) && paymentInfo?.additionalInformation && paymentInfo.additionalInformation.length > 0) {
			// Remove all additional information as basket is empty.
			dispatch({ type: 'INIT_PAYMENT_INFO', paymentInfo: { ...paymentInfo, additionalInformation: [] } });
		} else {
			dispatch({ type: 'INIT_PAYMENT_INFO', paymentInfo });
		}
	}
};

const clientInit = async (dispatch, getState) => {
	const { setup, campaign = {}, postParameters = {}, getParameters = {} } = getState().environment;
	const { type: locationType } = getState().location;
	const { selection = {}, giftCardSelection = {}, destroySelection = false, destroyGiftCardSelection = false } = getState().checkout;
	let receipt = false;
	if (locationType === 'RECEIPT') receipt = 'selection';
	if (locationType === 'GIFT_CARD_RECEIPT') receipt = 'giftCardSelection';
	let selectionData = {};
	let giftCardSelectionData = {};
	let token = null;
	let giftCardToken = null;

	const savedConsent = getCookie('cookies', true);
	if (savedConsent) {
		const consent = {};
		consent.functionality_storage = savedConsent.indexOf('functionality') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.ad_storage = savedConsent.indexOf('marketing') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.ad_user_data = savedConsent.indexOf('marketing') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.ad_personalization = savedConsent.indexOf('marketing') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.analytics_storage = savedConsent.indexOf('analytics') !== -1 || savedConsent.indexOf('all') !== -1 ? 'granted' : 'denied';
		consent.security_storage = 'granted';
		consent.personalization_storage = 'granted';

		dispatch({ type: 'COOKIE_CONSENT' });
		if (typeof window.gtag === 'function') window.gtag('consent', 'update', consent);
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		window.dataLayer.push({
			event: 'ConsentUpdated',
			values: {},
		});
	}

	dispatch({ type: 'LIST_ITEM_CACHE' });

	// Receipt component was not unmounted page was reloaded.
	if (destroySelection || (typeof document !== 'undefined' && getCookie('receiptShown'))) {
		dispatch({ type: 'DESTROY_SELECTION' });
		return dispatch({ type: 'START' });
	}
	if (destroyGiftCardSelection || (typeof document !== 'undefined' && getCookie('giftCardReceiptShown'))) {
		dispatch({ type: 'DESTROY_SELECTION', giftCardSelection: receipt });
		return dispatch({ type: 'START' });
	}

	if (!token && typeof localStorage !== 'undefined') {
		// transition to cookie based selection.
		token = localStorage.getItem('selection');
		if (token) {
			localStorage.removeItem('selection');
			setCookie('selection', token, 365);
		}
	}
	if (!token && selection.token) token = selection.token;
	if (!giftCardToken && giftCardSelection.token) giftCardToken = giftCardSelection.token;
	if (!token && typeof document !== 'undefined') token = getCookie('selection');
	if (!giftCardToken && typeof document !== 'undefined') giftCardToken = getCookie('giftCardSelection');

	if (token) {
		const selectionCache = JSON.parse(localStorage.getItem('selectionCache'));
		if (selectionCache) dispatch({ type: 'INIT_SELECTION_CACHE', initCache: selectionCache });
	}

	if (Object.keys(campaign).length > 0 && campaign.site) {
		selectionData = await fetchCheckout('/campaign-site', token, { uri: campaign.site }, 'PUT');
	}

	if (!receipt) {
		if (!getState()?.listing?.story) {
			const body = setup.previewCode ? { previewCode: setup.previewCode } : {};
			fetchData('listing/story', body).then((data) => (
				dispatch({ type: 'LISTING', data, listing: 'story' })
			)).catch((err) => {
				console.log('DEBUG: error retrieving story listing', err);
				Sentry.captureException(err);
			});
		}
		if (!getState()?.listing?.style) {
			const body = setup.previewCode ? { previewCode: setup.previewCode } : {};
			fetchData('listing/style', body).then((data) => (
				dispatch({ type: 'LISTING', data, listing: 'style' })
			)).catch((err) => {
				console.log('DEBUG: error retrieving style listing', err);
				Sentry.captureException(err);
			});
		}

		if (token) {
			// All other pages except for receipt
			selectionData = fetchCheckout('/selection', token, [], 'GET');

			try {
				selectionData = await selectionData;
			} catch (err) {
				console.log('An error occurred when trying to retrieve previous selection.', err);
				dispatch({ type: 'CHECKOUT_ERROR' });
			}

			if (selectionData.token) {
				dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
			}
		}

		if (giftCardToken) {
			// All other pages except for receipt
			giftCardSelectionData = fetchCheckout('/selection', giftCardToken, [], 'GET');

			try {
				giftCardSelectionData = await giftCardSelectionData;
			} catch (err) {
				console.log('An error occurred when trying to retrieve previous selection.', err);
				dispatch({ type: 'CHECKOUT_ERROR' });
			}

			if (giftCardSelectionData.token) {
				dispatch({ type: 'UPDATE_SELECTION', data: giftCardSelectionData, giftCardSelection: 'giftCardSelection' });
			}
		}
	} else if (receipt) {
		if (receipt === 'giftCardSelection') token = giftCardToken;
		// Get post parameters from store.
		// Receipt page
		if (token) {
			try {
				const paymentMethodFields = { ...getParameters, ...postParameters };
				selectionData = await fetchCheckout('/payment-result', token, { paymentMethodFields }, 'POST');
			} catch (err) {
				Sentry.withScope((scope) => {
					scope.setContext('customer', { token });
					Sentry.captureMessage('RECEIPT ERROR: fetch failed, redirected to selection/failed');
					Sentry.captureException(err);
				});
				return dispatch({ type: 'SELECTION_FAILED' });
			}
		} else {
			// Token has disappeared.
			// Not much to do about it and not neccessarily wrong.
			// User may have refreshed receipt page or tried to go back but was redirected to the receipt
			Sentry.captureMessage('RECEIPT WARNING: no token, redirected to start');

			return dispatch({ type: 'START' });
		}

		if (selectionData.token && selectionData.order && !selectionData.errors) {
			// Purchase succeeded
			dispatch({ type: 'RECEIPT_DATA', data: selectionData, giftCardSelection: receipt });
		} else {
			// Selection errored out.
			// Need to get the original selection so the customer can try again.
			Sentry.withScope((scope) => {
				scope.setContext('customer', { token });
				Sentry.captureMessage('RECEIPT ERROR: selection had errors, redirected to selection/failed');
			});

			selectionData = fetchCheckout('/selection', token, [], 'GET');

			try {
				selectionData = await selectionData;
			} catch (err) {
				console.log('An error occurred when trying to retrieve previous selection.', err);
				dispatch({ type: 'CHECKOUT_ERROR' });
			}

			if (selectionData.token) {
				dispatch({ type: 'UPDATE_SELECTION', data: selectionData, giftCardSelection: receipt });
			}

			updatePaymentInfo(dispatch, selectionData, selectionData.token);

			return dispatch({ type: 'SELECTION_FAILED' });
		}
	}

	// If selection is set to another country than default change country data to that of selection.
	if (selectionData.location && selectionData.location.country !== setup.country) {
		dispatch(changeCountry(setup.country));
	}

	// Ensure that we have the product cache for the current market and pricelist.
	// We load this in asyncronously because it's needed for search and will be needed for other pages if not already included.
	await retrieveProductCache(dispatch, getState);

	if (token) {
		const selectionCache = JSON.parse(localStorage.getItem('selectionCache'));
		if (selectionCache) {
			const productCache = getState().productCache[setup.market][setup.pricelist];
			const updatedProducts = Object.keys(selectionCache);
			const updatedSelectionCache = updatedProducts.reduce((acc, productKey) => ({ ...acc, [productKey]: deepClone(productCache.products[productKey]) }), {});
			dispatch({ type: 'UPDATE_SELECTION_CACHE', productCache: updatedSelectionCache, product: updatedProducts, selectionCache });
		}
	}

	let favourites = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('favourites')) : false;
	if (favourites) {
		const productCache = getState().productCache[setup.market][setup.pricelist];
		favourites = favourites.filter((favourite) => productCache.products[favourite] !== undefined);
		dispatch({ type: 'UPDATE_FAVOURITES', favourites });
	}

	clientUpdate(dispatch, getState, getState().pageStatus.pageType || '');

	updatePaymentInfo(dispatch, selectionData, token);

	dispatch({ type: 'SITE_UPDATED' });

	return getState();
};

const retrieveDepictProductCache = async (dispatch, getState, filterPath) => {
	const { setup } = getState().environment;
	const cache = getState().productCache[setup.market][setup.pricelist];

	if (cache.depictKeys[filterPath].productOrder.length === 0) {
		try {
			const depictBody = {
				market: setup.country.toLowerCase(),
				merchant: 'stutterheim',
				locale: 'en',
				limit: '250',
			};
			const depictProducts = await fetchDepict(`v3/listings/${cache.depictKeys[filterPath].listingId}/products`, depictBody, 'POST');

			if (depictProducts.cursor) {
				// paginate
			}
			const depictProductOrder = [];
			const depictProductCache = {};
			depictProducts.displays.forEach((d) => {
				if (typeof d.variant_index !== 'undefined' && d.variant_displays[d.variant_index]) {
					const vd = d.variant_displays[d.variant_index];
					depictProductOrder.push(`id_${vd.display_item_id}`);
					depictProductCache[`id_${vd.display_item_id}`] = { image: [...vd.image_urls.slice(0, 2)], listingId: d.product_listing_result_id };
				}
			});

			dispatch({ type: 'PRODUCT_CACHE_UPDATE', market: setup.market, pricelist: setup.pricelist, category: filterPath, depictProductOrder, depictProductCache });
		} catch (err) {
			console.log('DEBUG: failed to retrieve product listing from depict', err);
		}
	}
};

const checkProductPickers = async (startPageData, setup, dispatch, getState) => {
	if (startPageData.content.pageHasProducts) {
		const getProductPickers = hasTemplateInObj(startPageData.content.sections, 'acfProducts', true);
		const ids = getProductPickers.filter((picker) => picker.content.identifier || picker.content.category).reduce((acc, picker) => {
			if (picker.content.identifier) acc.push(picker.content.identifier);
			if (picker.content.category) acc = [...acc, picker.content.category.map((c) => c.en)];
			return acc;
		}, []);
		if (ids.length > 0) {
			await Promise.all(ids.map((id) => retrieveDepictProductCache(dispatch, getState, id)));
		}
	}
};

const retrieveProductCache = async (dispatch, getState) => {
	const { setup } = getState().environment;
	const { previewCode, country, market, pricelist, currency } = setup;

	if (getState()?.productCache?.[market]?.[pricelist]) return getState().productCache[market][pricelist];

	try {
		const body = previewCode ? { previewCode } : {};
		const productCachePromise = fetchData(`filter-cache/${market}/${pricelist}/`, body);

		const depictBody = {
			merchant: 'stutterheim',
			market: country.toLowerCase(),
			locale: 'en',
		};
		const depictListingsPromise = fetchDepict('v3/listings', depictBody);
		const categoryDataPromise = fetchData('all-categories');

		const [productCache, depictListings, categoryData] = await Promise.all([productCachePromise, depictListingsPromise, categoryDataPromise]);
		dispatch({ type: 'PRODUCT_CACHE', market, pricelist, setup, productCache, depictListings, categoryData });
	} catch (err) {
		console.log('DEBUG: product cache not loaded');
	}

	return dispatch({ type: 'UPDATE_COUNTRY', market, pricelist, country, currency });
};

const languagePrefix = '/:language?'; // '';
const useLanguage = (str = '') => `${languagePrefix}${str}`;

export default {
	CLIENT_INIT: {
		thunk: async (dispatch, getState) => clientInit(dispatch, getState).catch((err) => console.log('DEBUG: error initiating client site data', err)),
	},
	RETRIEVE_PRODUCT_CACHE: {
		thunk: async (dispatch, getState) => (
			retrieveProductCache(dispatch, getState)
		),
	},
	REFERRAL: {
		path: '/ref',
		thunk: async (dispatch, getState, bag) => {
			const { location, environment } = getState();
			const { getParameters = {}, setup } = environment;
			const { hash = '' } = bag.action.payload;
			let path = '';
			let search = '';
			const goTo = Object.keys(getParameters).reduce((acc, param) => (param.toLowerCase() === 'goto' ? getParameters[param] : acc), '');
			if (goTo) path = decodeURIComponent(goTo).replace(/^\/+/, '');
			if (path.indexOf('#') !== -1) path = path.slice(0, path.indexOf('#'));
			if (path.indexOf('?') !== -1) {
				search = path.slice(path.indexOf('?'));
				path = path.slice(0, path.indexOf('?'));
			} else if (location.search) {
				search = `?${location.search}`;
			}
			if (path === '/' || path === '') return dispatch(redirect({ type: 'START', payload: { language: setup.language, path: '', search, hash } }));
			return dispatch(redirect({ type: 'CATEGORY_PRODUCT_PAGE', payload: { language: setup.language, path, search, hash } }));
		},
	},
	CLEAR_COOKIES: {
		path: useLanguage('/clear-cookies'),
		thunk: async (dispatch) => {
			deleteAllCookies();
			return dispatch(redirect({ type: 'START' }));
		},
	},
	FAVOURITES: {
		path: useLanguage('/favourites/:favouriteList*'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const { favouriteList = '' } = location.payload;
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'favourites' }), {}) : path;

			clientUpdate(dispatch, getState);
			await retrieveProductCache(dispatch, getState);

			if (favouriteList) {
				favouriteList.split(',').forEach((productId) => {
					dispatch({ type: 'TOGGLE_FAVOURITE', productId, force: true });
				});
			}

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'favourites' });
		},
	},
	SEARCH: {
		path: useLanguage('/search'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, location } = getState();
			const { languages } = environment.setup;
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'search' }), {}) : path;
			let { hash = '' } = bag.action.payload;

			clientUpdate(dispatch, getState);

			hash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', hash });

			if (location.query && Object.keys(location.query).length > 0 && Object.keys(location.query)[0] !== '') {
				if (Object.keys(location.query)[0] === 'q') dispatch({ type: 'SEARCH_RESULTS', text: location.query.q });
				else dispatch({ type: 'SEARCH_RESULTS', text: Object.keys(location.query)[0] });
			}

			await retrieveProductCache(dispatch, getState);
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'search' });
		},
	},
	SELECTION: {
		path: useLanguage('/selection'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, location, pageData } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection' }), {}) : path;

			const body = getState().environment.setup.previewCode ? { previewCode: getState().environment.setup.previewCode } : {};
			const selectionPage = !pageData || !pageData.selection ? fetchData('selection', body) : pageData.selection;
			let selectionPageData;
			clientUpdate(dispatch, getState);

			await retrieveProductCache(dispatch, getState);

			try {
				selectionPageData = await selectionPage;
			} catch (err) {
				console.log('DEBUG: failed page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: selectionPageData, page: 'selection' });
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'selection' });
		},
	},
	SELECTION_FAILED: {
		path: useLanguage('/selection/failed'),
		thunk: async (dispatch, getState) => {
			const { environment, location, pageData } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection/failed' }), {}) : path;
			let failedPageData;

			const body = getState().environment.setup.previewCode ? { previewCode: getState().environment.setup.previewCode } : {};
			const failedPage = !pageData || !pageData['selection/failed'] ? fetchData('selection/failed', body) : pageData['selection/failed'];
			clientUpdate(dispatch, getState);

			try {
				failedPageData = await failedPage;
			} catch (err) {
				console.log('DEBUG: failed page data not retrieved');
			}

			dispatch({ type: 'PAGE_DATA', data: failedPageData, page: 'selection/failed' });
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'selection/failed' });
		},
	},
	RECEIPT: {
		path: useLanguage('/selection/receipt'),
		thunk: async (dispatch, getState) => {
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection/receipt' }), {}) : path;

			clientUpdate(dispatch, getState);

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'selection/receipt' });
		},
	},
	RECEIPT_DEBUG: {
		path: useLanguage('/selection/receipt/debug'),
		thunk: async (dispatch, getState) => {
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection/receipt/debug' }), {}) : path;

			clientUpdate(dispatch, getState);

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'selection/receipt/debug' });
		},
	},
	SELECTION_TRACKING: {
		path: useLanguage('/selection/tracking/:orderId?/:email?'),
		thunk: async (dispatch, getState) => {
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection/tracking' }), {}) : path;

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'selection/tracking' });
		},
	},
	SELECTION_RECOVER: {
		path: useLanguage('/selection/recover/:newToken*'),
		thunk: async (dispatch, getState) => {
			const { location, environment } = getState();
			const { newToken = '' } = location.payload;
			let { language = false } = location.payload;
			let { search = '' } = location;
			if (!language) language = environment.setup.language;
			if (search) search = `?${search}`;

			if (!newToken) {
				return dispatch(redirect({ type: 'START', payload: { language } }));
			}

			try {
				const selectionData = await fetchCheckout(`/selection/${newToken}`, false, {}, 'PUT');
				if (selectionData.token) {
					dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
					const payload = language === 'en' ? { search } : { language, search };
					return dispatch(redirect({ type: 'SELECTION', payload }));
				}
				return dispatch(redirect({ type: 'START', payload: { language, search } }));
			} catch (err) {
				return dispatch(redirect({ type: 'START', payload: { language, search } }));
			}
		},
	},
	SELECTION_PAYMENT_LINK: {
		path: useLanguage('/selection/link/:paymentLink*'),
		thunk: async (dispatch, getState) => {
			const { location, environment } = getState();
			const { selection = {} } = getState().checkout;
			const { paymentLink = '' } = location.payload;
			let { language = false } = location.payload;
			if (!language) language = environment.setup.language;

			let token = null;
			if (!token && selection.token) token = selection.token;
			if (!token && typeof document !== 'undefined') token = getCookie('selection');

			if (!paymentLink) {
				return dispatch(redirect({ type: 'START', payload: { language } }));
			}

			try {
				const paymentLinkResult = await fetchData('payment-links', { paymentLink });
				if (paymentLinkResult?.success && paymentLinkResult?.data) {
					const selectionData = await fetchCheckout(`/selection/${paymentLinkResult.data}`, token, {}, 'PUT');
					if (selectionData.token) {
						dispatch({ type: 'UPDATE_SELECTION', data: selectionData });
						const payload = language === 'en' ? {} : { language };
						return dispatch(redirect({ type: 'SELECTION', payload }));
					}
				}
				return dispatch(redirect({ type: 'START', payload: { language } }));
			} catch (err) {
				return dispatch(redirect({ type: 'START', payload: { language } }));
			}
		},
	},
	/*
	GIFT_CARD_RECEIPT: {
		path: useLanguage('/gift-card/receipt'),
		thunk: async (dispatch, getState) => {
			const { languages } = getState().environment.setup;
			clientUpdate(dispatch, getState);

			dispatch({ type: 'PAGE_PATHS', pageType: 'giftCardReceipt', paths: Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'gift-card/receipt' }), {}) });
		},
	},
	GIFT_CARD_RECEIPT_DEBUG: {
		path: useLanguage('/gift-card/receipt/debug'),
		thunk: async (dispatch, getState) => {
			const { languages } = getState().environment.setup;
			clientUpdate(dispatch, getState);

			dispatch({ type: 'PAGE_PATHS', pageType: 'giftCardReceiptDebug', paths: Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'gift-card/receipt/debug' }), {}) });
		},
	},
	GIFT_CARD: {
		path: useLanguage('/gift-card/:amount?'),
		thunk: async (dispatch, getState) => {
			const { location, environment, pageData } = getState();
			const { setup } = environment;
			const { amount = '0' } = location.payload;

			const body = setup.previewCode ? { previewCode: setup.previewCode } : {};
			const giftCardPage = !pageData || !pageData['gift-card'] ? fetchData('gift-card', body) : pageData['gift-card'];
			let giftCardPageData;
			clientUpdate(dispatch, getState);

			try {
				giftCardPageData = await giftCardPage;
			} catch (err) {
				console.log('DEBUG: failed page data not retrieved');
			}

			const giftCards = giftCardPageData.giftCards || {};
			const giftkey = Object.keys(giftCards).filter((gC) => giftCards[gC].type === 'anyAmount');
			if (giftkey.length > 0 && amount !== '0') {
				dispatch({ type: 'UPDATE_GIFT_CARD_AMOUNT', giftCard: giftkey[0], amount: amount.replace(/\D/g, '') });
			}

			dispatch({ type: 'PAGE_DATA', data: giftCardPageData, page: 'gift-card' });
			dispatch({ type: 'PAGE_PATHS', pageType: 'selection', paths: Object.keys(setup.languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'selection' }), {}) });
		},
	},
	*/
	ACCOUNT: {
		path: useLanguage('/account'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'account' }), {}) : path;

			clientUpdate(dispatch, getState);

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'account' });
		},
	},
	ACCOUNT_ORDER: {
		path: useLanguage('/account/order-:orderId*'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'account' }), {}) : path;

			clientUpdate(dispatch, getState);

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'accountOrder' });
		},
	},
	LOG_OUT: {
		path: useLanguage('/logout'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages, language } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'logout' }), {}) : path;

			// Remove token
			dispatch(logoutAccount());

			dispatch({ type: 'PAGE_LOADED', paths, pageType: 'logout' });
			return dispatch(redirect({ type: 'START', payload: { language, path: '/' } }));
		},
	},
	REMEMBER: {
		path: useLanguage('/remember'),
		thunk: async (dispatch, getState) => {
			dispatch({ type: 'PAGE_LOADING' });
			const { environment, location } = getState();
			const path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: 'remember' }), {}) : path;

			clientUpdate(dispatch, getState);

			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'remember' });
		},
	},
	START2: {
		path: '/',
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, pageData } = getState();
			const path = '';
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: '' }), {}) : path;

			// best guess that it's start page otherwise cached...
			const body = getState().environment.setup.previewCode ? { previewCode: getState().environment.setup.previewCode } : {};
			const startPage = !pageData || !pageData.start ? fetchData('', body) : pageData.start;
			let { hash = '' } = bag.action.payload;
			let startPageData;
			clientUpdate(dispatch, getState);

			hash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', hash });

			try {
				startPageData = await startPage;
			} catch (err) {
				console.log('DEBUG: start page data not retrieved');
			}

			await checkProductPickers(startPageData, environment.setup, dispatch, getState);

			dispatch({ type: 'PAGE_DATA', data: startPageData, page: 'start' });
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'start' });
		},
	},
	START: {
		path: '/:language([a-z]{2}-[a-z]{2})',
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			const { environment, pageData } = getState();
			const path = '';
			const { languages } = environment.setup;
			const paths = Object.keys(languages).length > 0 ? Object.keys(languages).reduce((acc, pathLanguage) => ({ ...acc, [pathLanguage]: '' }), {}) : path;

			// best guess that it's start page otherwise cached...
			const body = getState().environment.setup.previewCode ? { previewCode: getState().environment.setup.previewCode } : {};
			const startPage = !pageData || !pageData.start ? fetchData('', body) : pageData.start;
			let { hash = '' } = bag.action.payload;
			let startPageData;
			clientUpdate(dispatch, getState);

			hash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', hash });

			try {
				startPageData = await startPage;
			} catch (err) {
				console.log('DEBUG: start page data not retrieved');
			}

			await checkProductPickers(startPageData, environment.setup, dispatch, getState);

			dispatch({ type: 'PAGE_DATA', data: startPageData, page: 'start' });
			return dispatch({ type: 'PAGE_LOADED', paths, pageType: 'start' });
		},
	},
	CATEGORY_PRODUCT_PAGE: {
		path: useLanguage('/:path+'),
		thunk: async (dispatch, getState, bag) => {
			dispatch({ type: 'PAGE_LOADING', initiated: Date.now() });
			/*
				USE CASES:
				/women/clothing/dresses/harper - Product
				/women/clothing/harper - Product
				/women/clothing - Category
				/en/women/clothing - Category with localization
				/sv/dam/kläder - Category with right localization but wrong locale - redirect to english version.
				/en/page
				/page
			*/
			const { location, environment, pageData = {} } = getState();
			const { setup, magazineListing = {}, showSale = {} } = environment;
			const productListPages = ['category'];
			let { hash = '' } = bag.action.payload;
			let language = languagePrefix !== '' ? location.payload.language : false;
			// Clean any encoded URI characters
			let path = decodeURIComponent(location.payload.path).replace(/^\/+/, '');
			let search = '';

			// Remove any hashes from path
			if (path.indexOf('#') !== -1) path = path.slice(0, path.indexOf('#'));
			// Remove any queries from path
			if (path.indexOf('?') !== -1) {
				search = path.slice(path.indexOf('?'));
				path = path.slice(0, path.indexOf('?'));
			} else if (location.search) {
				search = `?${location.search}`;
			}

			// "language" exists. Is it a language or part of a path.
			if (language && !setup.languages[language]) {
				path = `${language}/${path}`;
				language = null;
			}

			// Get content for site.
			const body = environment.setup.previewCode ? { previewCode: environment.setup.previewCode } : {};
			const page = pageData[path] || fetchData(path, body);
			let listing = null;
			let pageDataReturn;
			let listingDataReturn;

			if (magazineListing?.meta && (magazineListing?.meta?.default?.uri === location.payload.path || (magazineListing?.meta[setup.language] && magazineListing.meta[setup.language].uri === location.payload.path))) {
				listing = getState()?.listing?.magazine || fetchData('listing/magazine', body);
			}

			try {
				[, pageDataReturn, listingDataReturn] = await Promise.all([retrieveProductCache(dispatch, getState), page, listing]);
			} catch (err) {
				console.log(`DEBUG: failed page data not retrieved. ${path}`, err);
			}

			if (pageDataReturn && pageDataReturn.is404) {
				dispatch({ type: NOT_FOUND });
				return dispatch({ type: 'PAGE_LOADED', paths: path, pageType: '404' });
			}

			if (pageDataReturn.paths && path !== '') {
				dispatch({ type: 'PAGE_PATHS', pageType: pageDataReturn.pageType, paths: pageDataReturn.paths });
				if (language !== false && path !== pageDataReturn.paths[language] && !pageDataReturn.content.is404 && pageDataReturn.paths[language]) {
					return dispatch(redirect({ type: 'CATEGORY_PRODUCT_PAGE', payload: { language, path: pageDataReturn.paths[language], search, hash } }));
				}
			}

			clientUpdate(dispatch, getState, pageDataReturn.pageType || '');

			dispatch({ type: 'PAGE_DATA', data: pageDataReturn, page: path });
			if (listingDataReturn) dispatch({ type: 'LISTING', data: listingDataReturn, listing: 'magazine' });

			const hashFilter = [];
			hash = typeof window !== 'undefined' && window.location.hash !== '' && window.location.hash !== '#' ? window.location.hash : hash;
			dispatch({ type: 'UPDATE_HASH', hash });
			if (hash) {
				hash.slice(1).split('&').forEach((filter) => {
					const filterParts = filter.split('=');
					if (filterParts.length === 1) {
						hashFilter.push({ [filterParts[0]]: true });
					} else {
						hashFilter.push({ [filterParts[0]]: filterParts[1].split(',') });
					}
				});
			}

			await checkProductPickers(pageDataReturn, setup, dispatch, getState);

			if (productListPages.indexOf(pageDataReturn.pageType) !== -1) {
				const filterPath = path;

				await retrieveDepictProductCache(dispatch, getState, filterPath);

				// Category
				dispatch({ type: 'RESET_FILTER' });

				if (showSale.restricted) hashFilter.push({ productsOnSale: true });
				dispatch({ type: 'FILTER', filterArray: [{ categories: filterPath }, ...hashFilter], force: true, setup, currentPath: filterPath });
			}

			return dispatch({ type: 'PAGE_LOADED', paths: pageDataReturn.paths || path, pageType: pageDataReturn.pageType || 'page' });
		},
	},
};
